import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { SampleDetailDrawer } from '@/features/sampling/components/SampleDetailDrawer';
import { formatINR } from '@/shared/utils/currency';

interface Entry { key: string; id: string; type: string; number: string; date?: string; status: string; viewUrl: string | null; total?: number; itemCount?: number; items?: { description: string; quantity: number; unitPrice: number }[] }
const labels: Record<string, string> = { quotation: 'Quotations', invoice: 'Invoices', proforma: 'Proformas', sample: 'Sample Requests' };
// No Clone here: a copy opened from history carried no link back to its
// source, and the business asked for it to go (quotations and samples too).

export function CustomerHistoryPanel({ recordId }: { recordId: string }) {
  const [type, setType] = useState('quotation');
  const [sampleId, setSampleId] = useState<string | null>(null);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['pipeline', 'customer-history', recordId],
    queryFn: async () => (await api.get<{ items: Entry[] }>(`/api/pipeline/${recordId}/customer-history`)).data,
  });
  const entries = data?.items || [];
  return <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xs">
    <h3 className="font-bold text-slate-900">Customer Document History</h3>
    <p className="mt-1 text-xs text-slate-500">Documents from all enquiries linked to this customer.</p>
    <div className="my-4 flex flex-wrap gap-2">{Object.entries(labels).map(([key, label]) =>
      <Button key={key} size="sm" variant={type === key ? 'primary' : 'outline'} onClick={() => setType(key)}>{label} ({entries.filter(e => e.type === key).length})</Button>
    )}</div>
    {isLoading ? <p>Loading customer history…</p> : isError ? <div>Could not load customer history. <Button onClick={() => void refetch()}>Retry</Button></div> :
      entries.filter(e => e.type === type).length === 0 ? <p className="text-sm text-slate-500">No {labels[type].toLowerCase()} found for this customer.</p> :
      <div className="max-h-96 space-y-2 overflow-auto">{entries.filter(e => e.type === type).map(e => <div key={e.key} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
        <div className="min-w-0">
          <strong className="text-sm">{e.number}</strong>
          <p className="text-xs text-slate-500">{e.date ? new Date(e.date).toLocaleDateString('en-IN') : ''} · {e.status}</p>
          {e.total !== undefined && <p className="text-xs font-semibold text-slate-700">{formatINR(e.total)}</p>}
          {e.items && e.items.length > 0 && <p className="text-data-xs text-slate-500">{e.items.length} item{e.items.length !== 1 ? 's' : ''}</p>}
        </div>
        <div className="flex gap-2">
          {e.type === 'sample' ? <Button size="sm" variant="outline" onClick={() => setSampleId(e.id)}>View</Button> : <a className="rounded-lg border px-3 py-2 text-sm" href={e.viewUrl!} target="_blank" rel="noreferrer">View</a>}
        </div>
      </div>)}</div>}
    <SampleDetailDrawer sampleId={sampleId} open={!!sampleId} onClose={() => setSampleId(null)} />
  </section>;
}
